import React, { useState, useEffect, useRef } from 'react';
import { Search, Save, Globe, ChevronRight, Image, Upload, Loader2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PAGES = [
  { id: 'home', name: 'Homepage', icon: '🏠' },
  { id: 'about', name: 'About Us', icon: '👥' },
  { id: 'pricing', name: 'Pricing', icon: '💰' },
  { id: 'contact', name: 'Contact', icon: '📞' },
  { id: 'search', name: 'Search', icon: '🔍' },
  { id: 'login', name: 'Login', icon: '🔐' },
  { id: 'register', name: 'Register', icon: '📝' },
];

const AdminSEO = () => {
  const [settings, setSettings] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [formData, setFormData] = useState({
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    og_image: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/extended/seo-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data.settings || []);
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectPage = (pageId) => {
    const pageSetting = settings.find(s => s.page_id === pageId);
    setSelectedPage(pageId);
    setFormData({
      meta_title: pageSetting?.meta_title || '',
      meta_description: pageSetting?.meta_description || '',
      meta_keywords: pageSetting?.meta_keywords || '',
      og_image: pageSetting?.og_image || ''
    });
  };

  const handleSave = async () => {
    if (!selectedPage) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/extended/seo-settings/${selectedPage}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ page_id: selectedPage, ...formData })
      });

      if (response.ok) {
        toast({ title: "SEO Settings Saved" });
        loadSettings();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const getCharCount = (text, max) => {
    const count = text?.length || 0;
    return (
      <span className={count > max ? 'text-red-500' : 'text-gray-400'}>
        {count}/{max}
      </span>
    );
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: "Invalid File", description: "Please upload PNG, JPG, GIF, or WebP", variant: "destructive" });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File Too Large", description: "Maximum file size is 5MB", variant: "destructive" });
      return;
    }

    setUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch(`${API_URL}/api/admin/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, og_image: data.url }));
        toast({ title: "Image Uploaded", description: "Social share image uploaded successfully" });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast({ title: "Upload Failed", description: "Could not upload image. Please try again.", variant: "destructive" });
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mint-500" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">SEO Settings</h1>
        <p className="text-gray-600">Optimize your pages for search engines</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Page List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader><CardTitle className="text-sm">Pages</CardTitle></CardHeader>
            <CardContent className="p-2">
              {PAGES.map((page) => {
                const pageSetting = settings.find(s => s.page_id === page.id);
                const isComplete = pageSetting?.meta_title && pageSetting?.meta_description;
                return (
                  <button
                    key={page.id}
                    onClick={() => selectPage(page.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                      selectedPage === page.id ? 'bg-mint-100 text-mint-700' : 'hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-xl">{page.icon}</span>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{page.name}</p>
                      <p className="text-xs text-gray-500">
                        {isComplete ? '✅ Optimized' : '⚠️ Needs SEO'}
                      </p>
                    </div>
                    {selectedPage === page.id && <ChevronRight className="w-4 h-4" />}
                  </button>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Editor */}
        <div className="lg:col-span-3">
          {selectedPage ? (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <span className="text-xl">{PAGES.find(p => p.id === selectedPage)?.icon}</span>
                  {PAGES.find(p => p.id === selectedPage)?.name} SEO
                </CardTitle>
                <Button size="sm" onClick={handleSave} disabled={saving} className="bg-mint-500 hover:bg-mint-600">
                  <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Meta Title */}
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label>Meta Title</Label>
                    {getCharCount(formData.meta_title, 60)}
                  </div>
                  <Input
                    value={formData.meta_title}
                    onChange={(e) => setFormData(p => ({ ...p, meta_title: e.target.value }))}
                    placeholder="Page title for search engines (50-60 chars ideal)"
                  />
                </div>

                {/* Meta Description */}
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label>Meta Description</Label>
                    {getCharCount(formData.meta_description, 160)}
                  </div>
                  <textarea
                    value={formData.meta_description}
                    onChange={(e) => setFormData(p => ({ ...p, meta_description: e.target.value }))}
                    placeholder="Brief description for search results (150-160 chars ideal)"
                    className="w-full h-24 p-3 border rounded-lg resize-none"
                  />
                </div>

                {/* Keywords */}
                <div className="space-y-2">
                  <Label>Meta Keywords (comma-separated)</Label>
                  <Input
                    value={formData.meta_keywords}
                    onChange={(e) => setFormData(p => ({ ...p, meta_keywords: e.target.value }))}
                    placeholder="muslim matrimony, halal dating, islamic marriage"
                  />
                </div>

                {/* OG Image */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Image className="w-4 h-4" /> Social Share Image (OG Image)
                  </Label>
                  <div className="space-y-2">
                    {formData.og_image && (
                      <div className="relative inline-block">
                        <img src={formData.og_image} alt="OG Preview" className="h-32 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => setFormData(p => ({ ...p, og_image: '' }))}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                        onChange={handleImageUpload}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingImage}
                        className="flex-1"
                      >
                        {uploadingImage ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                        ) : (
                          <><Upload className="w-4 h-4 mr-2" /> {formData.og_image ? 'Change Image' : 'Upload Image'}</>
                        )}
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500">Recommended: 1200x630px. PNG, JPG, WebP (max 5MB). This image shows when shared on social media.</p>
                  </div>
                </div>

                {/* Google Preview */}
                <div className="space-y-2 pt-4 border-t">
                  <Label>Google Search Preview</Label>
                  <div className="p-4 bg-white border rounded-lg">
                    <p className="text-blue-800 text-lg hover:underline cursor-pointer truncate">
                      {formData.meta_title || 'Page Title - Qbool'}
                    </p>
                    <p className="text-green-700 text-sm">
                      qbool.com/{selectedPage === 'home' ? '' : selectedPage}
                    </p>
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {formData.meta_description || 'Add a meta description to improve how this page appears in search results.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Select a page to edit SEO settings</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSEO;
